<template>
    <div>

        <div v-if="loading" class="text-center">
            <i class="fa fa-circle-o-notch fa-spin"></i> Loading
        </div>
        <div v-else>
            <vue-headful :title="subcontractor.subcontractor_name+ ' :: MeritEHS'"/>

            <div class="container d-print-none">
                <h4 class="py-3 mb-4">
                    <ol class="breadcrumb font-weight-bold m-0">
                        <template v-if="is_subcontractor">
                            <li class="breadcrumb-item font-weight-light">
                                <router-link to="/">
                                    <i class="fa fa-id-badge"></i> Dashboard
                                </router-link>
                            </li>
                            <li class="breadcrumb-item font-weight-light">
                                <router-link to="/subcontractors/qualification/view">
                                    {{ subcontractor.subcontractor_name }}
                                </router-link>
                            </li>
                        </template>
                        <template v-else>
                            <li class="breadcrumb-item font-weight-light">
                                <router-link :to="`/subcontractors`">
                                    <i class="fa fa-id-badge"></i> Subcontractors
                                </router-link>
                            </li>
                            <li class="breadcrumb-item font-weight-light">
                                <router-link :to="`/subcontractors/qualification/view/${subcontractor.subcontractor_id}`">
                                    {{ subcontractor.subcontractor_name }}
                                </router-link>
                            </li>
                        </template>

                        <li class="breadcrumb-item active">
                            <i class="fa fa-check-square"></i> Pre-Qualification
                            <span v-if="is_subcontractor" :class="`text-${subcontractor.status_color}`">{{ subcontractor.status_name }}</span>
                        </li>
                    </ol>
                </h4>
            </div>
            <div class="container bg-white d-print-none py-4 mb-4">
                <div class="alert alert-info">
                    <strong>How to Export as PDF:</strong>
                    <ol>
                        <li>On your browser, there should be a "File" or "Options" tab with a "Print" option. You may also hold down the "Ctrl" key and press the "P" key.</li>
                        <li>This will bring up the print page options box. There should be an option called "Select Printer" or "Destination".</li>
                        <li>Select "Save as PDF" or "Adobe PDF".</li>
                        <li>Click "Save" or "Print". This will open your file explorer and let you save it to your computer as a PDF.</li>
                    </ol>

                    <div>
                        <strong>PDF Options:</strong>
                        <ol>
                            <li class="mb-1" v-if="pdfLogo">
                                <span>Show/Hide Logo:</span>
                                <select v-model="showLogo">
                                    <option :value="true">Show Logo</option>
                                    <option :value="false">Hide Logo</option>
                                </select>
                                <span class="small">Show/Hide your company logo on the Inspection PDF report.</span>
                            </li>
                        </ol>
                    </div>
                </div>
            </div>

            <div class="container bg-white py-4">
                <div class="row" v-if="pdfLogo && showLogo === true" style="margin-bottom: 50px;">
                    <div class="col-md-3">
                        <img :src="pdfLogo" class="img-fluid" />
                    </div>
                </div>

                <h5 class="section-header" style="margin-top: 20px !important;">
                    <span class="section-header-muted">Subcontractor:</span> {{ subcontractor.subcontractor_name }}
                </h5>

                <div class="mb-3">
                    <div class="row mb-1">
                        <div class="col-2 text-muted">Status:</div>
                        <div class="col-8">
                            <span class="mb-0" style="display: inline-block">
                                <strong :class="`text-${subcontractor.status_color}`">{{ subcontractor.status_name }}</strong>
                            </span>
                        </div>
                    </div>
                    <div class="row mb-1">
                        <div class="col-2 text-muted">Name:</div>
                        <div class="col-md-8">{{ subcontractor.subcontractor_name }}</div>
                    </div>
                    <div class="row mb-1">
                        <div class="col-2 text-muted">Created:</div>
                        <div class="col-md-8">{{ subcontractor.created_at | moment('timezone', this.$store.state.user.timezone, "MM/DD/YYYY") }}</div>
                    </div>
                    <div class="row mb-1">
                        <div class="col-2 text-muted">Last Updated:</div>
                        <div class="col-md-8">{{ subcontractor.updated_at | moment("MM/DD/YYYY") }}</div>
                    </div>
                    <div class="row mb-1">
                        <div class="col-2 text-muted">Approved Until:</div>
                        <div class="col-md-8">{{ subcontractor.valid_until | moment("MM/DD/YYYY") }}</div>
                    </div>
                </div>

                <!-- Section 1 -->
                <h5 class="section-header">
                    <span class="section-header-muted">Section 1:</span> General Information
                    <span v-if="sectionStatus(1).status_name">
                        &nbsp;-&nbsp;<span style="font-weight: normal;" :class="`text-${sectionStatus(1).status_color}`">{{ sectionStatus(1).status_name != null ? 'Section ' + sectionStatus(1).status_name : '' }}</span>
                    </span>
                </h5>
                <div class="row mb-1">
                    <div class="col-3 text-muted">Prequalified through 3rd party?:</div>
                    <div class="col-8">
                        <span v-if="subcontractor.prequalified_3rd_party == 1" class="text-success">
                            Yes
                        </span>
                        <span v-else-if="subcontractor.prequalified_3rd_party == 0" class="text-default">
                            No
                        </span>
                    </div>
                </div>
                <div class="row">
                    <div class="col-sm-3 col-12 text-muted">Prequalified 3rd Party Certification:</div>
                    <div class="col-md-8">
                        <file-list :basicView="true" :files="subcontractor.prequalified_file ? [subcontractor.prequalified_file] : []" />
                    </div>
                </div>
                <hr/>

                <bid-list :subcontractorId="subcontractor.subcontractor_id" />

                <!-- Section 2 -->
                <h5 class="section-header">
                    <span class="section-header-muted">Section 2:</span> Company Information
                    <span v-if="sectionStatus(2).status_name">
                        &nbsp;-&nbsp;<span style="font-weight: normal;" :class="`text-${sectionStatus(2).status_color}`">{{ sectionStatus(2).status_name != null ? 'Section ' + sectionStatus(2).status_name : '' }}</span>
                    </span>
                </h5>
                <div class="row">
                    <div class="col-5">
                        <div class="row mb-1">
                            <label class="col-4 text-muted">Company Name:</label>
                            <div class="col-8">
                                {{ subcontractor.subcontractor_name }}
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">Address:</label>
                            <div class="col-8">
                                {{ subcontractor.address }}
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">City:</label>
                            <div class="col-8">
                                {{ subcontractor.city }}
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">State:</label>
                            <div class="col-8">
                                {{ subcontractor.state }}
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">Zip:</label>
                            <div class="col-8">
                                {{ subcontractor.zip }}
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">Phone:</label>
                            <div class="col-8">
                                {{ subcontractor.phone }}
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">Fax:</label>
                            <div class="col-8">
                                {{ subcontractor.fax }}
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">Website:</label>
                            <div class="col-8">
                                <a :href="(subcontractor.website.indexOf('http://') === -1 && subcontractor.website.indexOf('https://') === -1) ? `http://${subcontractor.website}` : subcontractor.website" v-if="subcontractor.website" target="_blank">{{ subcontractor.website }}</a>
                            </div>
                        </div>
                    </div>
                    <div class="col-7">
                        <div class="row mb-1">
                            <label class="col-4 text-muted">Company Contact Name:</label>
                            <div class="col-8">
                                {{ subcontractor.company_contact_name }}
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">Company Contact Phone:</label>
                            <div class="col-8">
                                {{ subcontractor.company_contact_phone }}
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">Company Contact Email:</label>
                            <div class="col-8">
                                {{ subcontractor.company_contact_email }}
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">Office Type:</label>
                            <div class="col-8">
                                {{ subcontractor.office_name }}
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">Other Names:</label>
                            <div class="col-8">
                                <span v-if="subcontractor.has_other_names == 1" class="text-success">
                                    Yes
                                </span>
                                <span v-else-if="subcontractor.has_other_names == 0" class="text-default">
                                    No
                                </span>
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">Other Operating Names:</label>
                            <div class="col-8">
                                {{ subcontractor.other_names }}
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">Business Start Date:</label>
                            <div class="col-8">
                                {{ subcontractor.business_start_date | moment("MM/YYYY") }}
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">Business Age:</label>
                            <div class="col-8">
                                {{ subcontractorAge }} years
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">Parent Company:</label>
                            <div class="col-8">
                                {{ subcontractor.parent_company }}
                            </div>
                        </div>
                    </div>
                </div>
                <hr/>

                <sub-designation-list :printMode="true" :subcontractorId="subcontractor.subcontractor_id" />
                <hr/>

                <sub-license-list :printMode="true" :subcontractorId="subcontractor.subcontractor_id" />

                <!-- Section 3 -->
                <h5 class="section-header">
                    <span class="section-header-muted">Section 3:</span> Work Performed
                    <span v-if="sectionStatus(3).status_name">
                        &nbsp;-&nbsp;<span style="font-weight: normal;" :class="`text-${sectionStatus(3).status_color}`">{{ sectionStatus(3).status_name != null ? 'Section ' + sectionStatus(3).status_name : '' }}</span>
                    </span>
                </h5>

                <div class="row">
                    <div class="col-4">
                        <label class="text-muted">Trades:</label>
                        <ul>
                            <li v-for="trade in trades">{{ trade }}</li>
                            <li v-if="subcontractor.other_trades"><b>Other:</b> {{ subcontractor.other_trades }}</li>
                        </ul>
                    </div>
                    <div class="col-4">
                        <label class="text-muted">Geographic Areas Willing to Work:</label>
                        <ul>
                            <li v-for="area in areas">{{ area }}</li>
                            <li v-if="subcontractor.other_areas"><b>Other:</b> {{ subcontractor.other_areas }}</li>
                        </ul>
                    </div>
                    <div class="col-4">
                        <label class="text-muted">State Registrations:</label>
                        <ul>
                            <li v-for="reg in registrations">{{ reg }}</li>
                            <li v-if="subcontractor.other_bus_registrations"><b>Other:</b> {{ subcontractor.other_bus_registrations }}</li>
                        </ul>
                    </div>
                </div>

                <!-- Section 4 -->
                <h5 class="section-header">
                    <span class="section-header-muted">Section 4:</span> Financial Information
                    <span v-if="sectionStatus(4).status_name">
                        &nbsp;-&nbsp;<span style="font-weight: normal;" :class="`text-${sectionStatus(4).status_color}`">{{ sectionStatus(4).status_name != null ? 'Section ' + sectionStatus(4).status_name : '' }}</span>
                    </span>
                </h5>

                <div class="row">
                    <div class="col-6">
                        <div class="row mb-1">
                            <label class="col-4 text-muted">Company Structure:</label>
                            <div class="col-8">
                                {{ subcontractor.comp_structure_name }}
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">Dun &amp; Bradstreet Number:</label>
                            <div class="col-8">
                                {{ subcontractor.db_number }}
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">Name of Your Company's Bank:</label>
                            <div class="col-8">
                                {{ subcontractor.bank_name }}
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">Bank Address:</label>
                            <div class="col-8">
                                {{ subcontractor.bank_address }}
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">Bank Contact Name &amp; Phone:</label>
                            <div class="col-8">
                                {{ subcontractor.bank_contact }}
                            </div>
                        </div>
                    </div>
                    <div class="col-6">
                        <div class="row mb-1">
                            <label class="col-4 text-muted">Federal ID# or SS#:</label>
                            <div class="col-8">
                                {{ subcontractor.company_id_num }}
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">IRS Form W9:</label>
                            <div class="col-8">
                                <file-list :basicView="true" :files="subcontractor.irs_w9 ? [subcontractor.irs_w9] : []" />
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">Has Your Company Performed Past Work:</label>
                            <div class="col-8">
                                {{ subcontractor.has_previous_projects == 1 ? 'Yes' : 'No' }}
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">Past Projects:</label>
                            <div class="col-8">
                                {{ subcontractor.other_projects }}
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Section 5 -->
                <h5 class="section-header">
                    <span class="section-header-muted">Section 5:</span> Health, Safety & Environmental
                    <span v-if="sectionStatus(5).status_name">
                        &nbsp;-&nbsp;<span style="font-weight: normal;" :class="`text-${sectionStatus(5).status_color}`">{{ sectionStatus(5).status_name != null ? 'Section ' + sectionStatus(5).status_name : '' }}</span>
                    </span>
                </h5>

                <div class="row">
                    <div class="col-5">
                        <div class="row mb-1">
                            <label class="col-4 text-muted">Current Number of Employees:</label>
                            <div class="col-8">
                                {{ subcontractor.company_size }}
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">Company Safety Representative Name:</label>
                            <div class="col-8">
                                {{ subcontractor.safety_rep_name }}
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">HSE Credentials:</label>
                            <div class="col-8">
                                {{ subcontractor.hse_credentials }}
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">Years of Experience:</label>
                            <div class="col-8">
                                {{ subcontractor.years_of_experience }}
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">Company Safety Representative Phone:</label>
                            <div class="col-8">
                                {{ subcontractor.safety_rep_phone }}
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">Company Safety Representative Email:</label>
                            <div class="col-8">
                                {{ subcontractor.safety_rep_email }}
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col text-muted">Has Written Safety &amp; Health Program:</label>
                            <div class="col-md-6">
                                <span v-if="subcontractor.has_written_program == 1" class="text-success">
                                    Yes
                                </span>
                                <span v-else-if="subcontractor.has_written_program == 0" class="text-default">
                                    No
                                </span>
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">HSE Program File:</label>
                            <div class="col-8">
                                <file-list :basicView="true" :files="subcontractor.program_file ? [subcontractor.program_file] : []" />
                            </div>
                        </div>
                        <div class="mt-4">
                            <label class="text-muted">Field Craft Personnel Training:</label><br/>
                            <ul>
                                <li v-for="training in fieldTrainings">{{ training }}</li>
                            </ul>
                        </div>
                    </div>
                    <div class="col-7">
                        <div class="row mb-1">
                            <label class="col text-muted text-right">Safety Orientation:</label>
                            <div class="col-md-6">
                                <span v-if="subcontractor.has_safety_orientation == 1" class="text-success">
                                    Yes
                                </span>
                                <span v-else-if="subcontractor.has_safety_orientation == 0" class="text-default">
                                    No
                                </span>
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col text-muted text-right">Competent Person Training:</label>
                            <div class="col-md-6">
                                <span v-if="subcontractor.has_person_training == 1" class="text-success">
                                    Yes
                                </span>
                                <span v-else-if="subcontractor.has_person_training == 0" class="text-default">
                                    No
                                </span>
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col text-muted text-right">Employee Safety Meetings:</label>
                            <div class="col-md-6">
                                <span v-if="subcontractor.has_safety_meetings == 1" class="text-success">
                                    Yes - Frequency: {{ subcontractor.meeting_frequency }}
                                </span>
                                <span v-else-if="subcontractor.has_safety_meetings == 0" class="text-default">
                                    No
                                </span>
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col text-muted text-right">Title of Safety Meeting Host:</label>
                            <div class="col-md-6">
                                {{ subcontractor.meeting_host_title }}
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col text-muted text-right">Substance Abuse Policy:</label>
                            <div class="col-md-6">
                                <span v-if="subcontractor.substance_abuse_policy == 1" class="text-success">
                                    Yes
                                </span>
                                <span v-else-if="subcontractor.substance_abuse_policy == 0" class="text-default">
                                    No
                                </span>
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col text-muted text-right">EMR Letter:</label>
                            <div class="col-md-6">
                                <file-list :basicView="true" :files="subcontractor.emr_file ? [subcontractor.emr_file] : []" />
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col text-muted text-right">Safety Differentiators:</label>
                            <div class="col-md-6">
                                {{ subcontractor.safety_differentiators }}
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col text-muted text-right">Testing Frequency:</label>
                            <div class="col-md-6">
                                <ul>
                                    <li v-for="testing in testings">{{ testing }}</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                <hr/>
                <sub-osha-years-list :subcontractorId="subcontractor.subcontractor_id" />

                <hr/>
                <osha-citation-list :subcontractorId="subcontractor.subcontractor_id" />

                <!-- Section 6 -->
                <h5 class="section-header">
                    <span class="section-header-muted">Section 6:</span> Insurance Information
                    <span v-if="sectionStatus(6).status_name">
                        &nbsp;-&nbsp;<span style="font-weight: normal;" :class="`text-${sectionStatus(6).status_color}`">{{ sectionStatus(6).status_name != null ? 'Section ' + sectionStatus(6).status_name : '' }}</span>
                    </span>
                </h5>
                <div class="row mb-1">
                    <label class="col-3 text-muted">Certificate of Insurance:</label>
                    <div class="col-9">
                        <file-list :basicView="true" :files="subcontractor.insurance_file ? [subcontractor.insurance_file] : []" />
                    </div>
                </div>
                <div class="row mb-1">
                    <label class="col-3 text-muted">Insurance Company &amp; Info:</label>
                    <div class="col-9">
                        {{ subcontractor.insurance_info }}
                    </div>
                </div>

                <!-- Section 7 -->
                <h5 class="section-header">
                    <span class="section-header-muted">Section 7:</span> Bonding Information
                    <span v-if="sectionStatus(7).status_name">
                        &nbsp;-&nbsp;<span style="font-weight: normal;" :class="`text-${sectionStatus(7).status_color}`">{{ sectionStatus(7).status_name != null ? 'Section ' + sectionStatus(7).status_name : '' }}</span>
                    </span>
                </h5>

                <div class="row">
                    <div class="col-6">
                        <div class="row mb-1">
                            <label class="col text-muted">Bonding Company:</label>
                            <div class="col-md-6">
                                {{ subcontractor.bonding_company }}
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">Copy of Bonding Letter:</label>
                            <div class="col-8">
                                <file-list :basicView="true" :files="subcontractor.bonding_file ? [subcontractor.bonding_file] : []" />
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">Bonding Agent &amp; Firm:</label>
                            <div class="col-8">
                                {{ subcontractor.bonding_agent_firm }}
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">Phone:</label>
                            <div class="col-8">
                                {{ subcontractor.bonding_phone }}
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">Previous Bonding Companies:</label>
                            <div class="col-8">
                                {{ subcontractor.bonding_previous_companies }}
                            </div>
                        </div>
                    </div>
                    <div class="col-6">
                        <div class="row mb-1">
                            <label class="col-4 text-muted">Max. Overall Bonding Capacity:</label>
                            <div class="col-8">
                                {{ subcontractor.bonding_max_capacity }}
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">Max. Bonding Capacity Per Contract:</label>
                            <div class="col-8">
                                {{ subcontractor.bonding_max_contract }}
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">Bonding Company Start Date:</label>
                            <div class="col-8">
                                {{ subcontractor.bonding_start_date }}
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">Years With Bonding Company:</label>
                            <div class="col-8">
                                {{ bondingAge || 0 }} Years
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Section 8 -->
                <h5 class="section-header">
                    <span class="section-header-muted">Section 8:</span> Past Performance
                    <span v-if="sectionStatus(8).status_name">
                        &nbsp;-&nbsp;<span style="font-weight: normal;" :class="`text-${sectionStatus(8).status_color}`">{{ sectionStatus(8).status_name != null ? 'Section ' + sectionStatus(8).status_name : '' }}</span>
                    </span>
                </h5>
                <div class="row">
                    <div class="col-5">
                        <div class="row mb-1">
                            <label class="col-10 text-muted text-right">EComplaint Filed by Licensing/Regulatory Agency:</label>
                            <div class="col-2">
                                <span v-if="subcontractor.has_official_complaint == 1" class="text-danger">
                                    Yes
                                </span>
                                <span v-else-if="subcontractor.has_official_complaint == 0" class="text-default">
                                    No
                                </span>
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-10 text-muted text-right">Contract Terminated for Cause:</label>
                            <div class="col-2">
                                <span v-if="subcontractor.has_terminated_contract == 1" class="text-danger">
                                    Yes
                                </span>
                                <span v-else-if="subcontractor.has_terminated_contract == 0" class="text-default">
                                    No
                                </span>
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-10 text-muted text-right">Failed to Complete Work:</label>
                            <div class="col-2">
                                <span v-if="subcontractor.has_failed_work == 1" class="text-danger">
                                    Yes
                                </span>
                                <span v-else-if="subcontractor.has_failed_work == 0" class="text-default">
                                    No
                                </span>
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-10 text-muted text-right">Judgments or Claims Pending:</label>
                            <div class="col-2">
                                <span v-if="subcontractor.has_pending_claims == 1" class="text-danger">
                                    Yes
                                </span>
                                <span v-else-if="subcontractor.has_pending_claims == 0" class="text-default">
                                    No
                                </span>
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-10 text-muted text-right">Involved in Arbitration Proceedings:</label>
                            <div class="col-2">
                                <span v-if="subcontractor.has_arbitration == 1" class="text-danger">
                                    Yes
                                </span>
                                <span v-else-if="subcontractor.has_arbitration == 0" class="text-default">
                                    No
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="col-7">
                        <label class="text-muted">Explain All "Yes" Responses:</label><br>
                        {{ subcontractor.past_performance }}
                    </div>
                </div>

                <!-- Section 9 -->
                <h5 class="section-header">
                    <span class="section-header-muted">Section 9:</span> References
                    <span v-if="sectionStatus(9).status_name">
                        &nbsp;-&nbsp;<span style="font-weight: normal;" :class="`text-${sectionStatus(9).status_color}`">{{ sectionStatus(9).status_name != null ? 'Section ' + sectionStatus(9).status_name : '' }}</span>
                    </span>
                </h5>
                <references-list :printMode="true" :subcontractorId="subcontractor.subcontractor_id"></references-list>

                <!-- Section 10 -->
                <h5 class="section-header">
                    <span class="section-header-muted">Section 10:</span> EEO & Eligibility To Work
                    <span v-if="sectionStatus(10).status_name">
                        &nbsp;-&nbsp;<span style="font-weight: normal;" :class="`text-${sectionStatus(10).status_color}`">{{ sectionStatus(10).status_name != null ? 'Section ' + sectionStatus(10).status_name : '' }}</span>
                    </span>
                </h5>
                <div class="row">
                    <div class="col-6">
                        <div class="row mb-1">
                            <label class="col-4 text-muted">(1)(a) Written EEO Policy:</label>
                            <div class="col-8">
                                <span v-if="subcontractor.has_eeo_policy == 1" class="text-success">
                                    Yes
                                </span>
                                <span v-else-if="subcontractor.has_eeo_policy == 0" class="text-default">
                                    No
                                </span>
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">(1)(b) Anti-Harassment Policy:</label>
                            <div class="col-8">
                                <span v-if="subcontractor.has_harassment_policy == 1" class="text-success">
                                    Yes
                                </span>
                                <span v-else-if="subcontractor.has_harassment_policy == 0" class="text-default">
                                    No
                                </span>
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">(1)(c) Employee Complaint Procedure:</label>
                            <div class="col-8">
                                <span v-if="subcontractor.has_complaint_procedure == 1" class="text-success">
                                    Yes
                                </span>
                                <span v-else-if="subcontractor.has_complaint_procedure == 0" class="text-default">
                                    No
                                </span>
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">(1)(d) Designated EEO Officer:</label>
                            <div class="col-8">
                                <span v-if="subcontractor.has_eeo_officer == 1" class="text-success">
                                    Yes
                                </span>
                                <span v-else-if="subcontractor.has_eeo_officer == 0" class="text-default">
                                    No
                                </span>
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">EEO Officer Credentials:</label>
                            <div class="col-8">
                                {{ subcontractor.eeo_officer_credentials }}
                            </div>
                        </div>
                    </div>
                    <div class="col-6">
                        <div class="row mb-1">
                            <label class="col-4 text-muted">Copy of EEO Policies:</label>
                            <div class="col-8">
                                <file-list :basicView="true" :files="subcontractor.eeo_file ? [subcontractor.eeo_file] : []" />
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">(2)(a) Verify Employee Eligibility:</label>
                            <div class="col-8">
                                <span v-if="subcontractor.has_verify_employees == 1" class="text-success">
                                    Yes
                                </span>
                                <span v-else-if="subcontractor.has_verify_employees == 0" class="text-default">
                                    No
                                </span>
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">(2)(b) Maintain of Compliance Records:</label>
                            <div class="col-8">
                                <span v-if="subcontractor.has_compliance_records == 1" class="text-success">
                                    Yes
                                </span>
                                <span v-else-if="subcontractor.has_compliance_records == 0" class="text-default">
                                    No
                                </span>
                            </div>
                        </div>
                        <div class="row mb-1">
                            <label class="col-4 text-muted">(2)(c) E-Verify ID Number:</label>
                            <div class="col-8">
                                {{ subcontractor.e_verify_id }}
                            </div>
                        </div>
                    </div>
                </div>

                <hr>

                <!-- Additional Remarks -->
                <h5 class="section-header">
                    Additional Notes
                </h5>
                <div>
                    {{ subcontractor.additional_notes }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import moment from 'moment-timezone';

    export default {
        data() {
            return {
                userTimezone: this.$store.state.user.timezone,
                loading: true,
                showLogo: true,
                subcontractor_id: this.$route.params.id,
                subcontractor: {},
                columns: ['sub_approval_id', 'client_name', 'approval_status_name', 'approval_created_at'],
                trades: [],
                areas: [],
                registrations: [],
                files: [],
                fieldTrainings: [],
                testings: [],
                users: [],
                statusHistory: [],
                current_status_section: null,
                is_subcontractor: this.$store.state.user.subcontractor_id,
                pdfLogo: null,

                file_columns: ['file_id', 'file_name', 'section_number', 'user_id', 'created_at'],
                file_options: {
                    headings: {
                        file_id: 'Actions',
                        file_name: 'File Name',
                        section_number: 'Section',
                        user_id: 'Uploader',
                        created_at: 'Uploaded Date',
                    },
                    perPageValues: [],
                    sortable: ['file_name', 'section_number'],
                    columnsDropdown: false,
                    uniqueKey: 'file_id',
                },
            }
        },

        computed: {

            subcontractorAge(){
                //Make sure we have a valid date
                if(!this.subcontractor.business_start_date)
                {
                    return '';
                }

                //Now
                var now = moment();
                var start = moment(this.subcontractor.business_start_date);

                //Send it
                return parseFloat(now.diff(start, 'days') / 365).toFixed(2);
            },

            bondingAge(){
                //Make sure we have a valid date
                if(!this.subcontractor.bonding_start_date)
                {
                    return '';
                }

                //Now
                var now = moment();
                var start = moment(this.subcontractor.bonding_start_date);

                //Send it
                return parseFloat(now.diff(start, 'days') / 365).toFixed(2);
            },

            sectionStatuses() {
                return this.statusHistory.filter(e => e.is_current == 1);
            },

            files_array() {
                // This doesn't work in IE...
                // return Object.values(this.files).flat();

                let return_files = [];

                for (let i in this.files)
                {
                    return_files = return_files.concat(this.files[i]);
                }

                return return_files;
            },
        },

        methods: {

            subcontractorPartsUpdated: function(data){
                //Update the cost
                this.subcontractor.cost = data.subcontractor_cost;

                //Redraw the table
                this.$refs.subcontractorParts.refreshData();
            },

            getSubcontractor: function() {
                //Get the data
                this.$http
                    .get(`subcontractors/qualification/view${this.subcontractor_id ? '/'+this.subcontractor_id : ''}`, {pdf: true})
                    .then(response => {
                        //Set all the data
                        this.subcontractor = response.data.subcontractor;
                        this.trades = response.data.trades;
                        this.areas = response.data.areas;
                        this.registrations = response.data.registrations;
                        this.files = response.data.files;
                        this.fieldTrainings = response.data.fieldTrainings;
                        this.testings = response.data.testings;
                        this.users = response.data.users;
                        this.statusHistory = response.data.statusHistory;
                        this.pdfLogo = response.data.pdfLogo;

                        //Set Designations
                        this.$store.commit('setBids', response.data.bids);
                        this.$store.commit('setCitations', response.data.citations);
                        this.$store.commit('setDesignations', response.data.designations);
                        this.$store.commit('setLicenses', response.data.licenses);
                        this.$store.commit('setSafetyYears', response.data.years);
                        this.$store.commit('setReferences', response.data.references);

                        //Set loading
                        this.loading = false;
                    })
                    .catch(response => {

                    });
            },

            approvalAction(){
                //Get the action
                this.$refs.subStatusModal.setModel({
                    status_id: this.subcontractor.status_id,
                    comment: ''
                });
                this.$refs.subStatusModal.showModal();
            },

            approvalActionSection(section_number){
                let section_status = this.sectionStatus(section_number);
                var status_id = '';
                if (section_status)
                {
                    status_id = section_status.status_id;
                }

                //Get the action
                this.$refs.subSectionStatusModal.setSection(section_number);
                this.$refs.subSectionStatusModal.setModel({
                    status_id: status_id,
                    comment: ''
                });
                this.current_status_section = section_number;
                this.$refs.subSectionStatusModal.showModal();
            },

            filteredOrderedHistory(section_number = null) {
                return this.statusHistory.slice().reverse().filter(e => {
                    return e.section_number == section_number;
                })
            },

            sectionStatus(section_number) {
                let statuses = this.sectionStatuses.filter(e => {
                    return e.section_number == section_number;
                })

                if (statuses.length == 0)
                    return {'status_color': null, 'status_name': null}

                else return statuses[0];
            },

            sectionFiles(section_number) {
                return this.files[section_number];
            },
        },
        mounted() {
            //Get the subcontractors
            this.getSubcontractor();
        }
    }
</script>
<style scoped>
    .section-header {
        padding-bottom: 10px;
        border-bottom: 1px solid rgba(24, 28, 33, 0.06);
        margin-bottom: 20px;
        margin-top: 80px;
    }

    .section-header-muted {
        font-weight: normal;
        color: #a3a4a6 !important;
    }

    .break-protect {
        page-break-inside: avoid;
    }
    .body {
        margin: 25mm;
    }
    @page {
        margin-top: 1in;
        margin-bottom: 1in;
    }
</style>